/**
 * Settings — theme and signature preferences. Everything else (account,
 * keys, connectors) lands later. Language switcher removed — site is
 * English-only.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Card, CardSubtitle, CardTitle } from '@/components/Card';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  SignModeToggle,
  getStoredSignMode,
  setStoredSignMode,
  type SignMode,
} from '@/components/SignModeToggle';

export function SettingsPage() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-ink">
          {t('nav.settings')}
        </h1>
      </header>
      <Card>
        <CardTitle>{t('theme.toggle')}</CardTitle>
        <CardSubtitle>{t('theme.light')} · {t('theme.dark')}</CardSubtitle>
        <div className="mt-3 flex items-center gap-3">
          <ThemeToggle />
        </div>
      </Card>
      <Card>
        <CardTitle>{t('settings.embedded_proof_title')}</CardTitle>
        <CardSubtitle>{t('settings.embedded_proof_subtitle')}</CardSubtitle>
        <div className="mt-4 flex flex-col gap-4">
          <EmbeddedProofPreferences />
        </div>
      </Card>
    </div>
  );
}

function EmbeddedProofPreferences() {
  const { t } = useTranslation();
  const enabled = import.meta.env.VITE_EMBEDDED_PROOF_UI_ENABLED !== 'false';
  if (!enabled) return null;

  const [sparkMode, setSparkMode] = useState<SignMode>(() => getStoredSignMode('spark'));

  const handleChange = (m: SignMode) => {
    setSparkMode(m);
    setStoredSignMode('spark', m);
  };

  const handleReset = () => {
    setStoredSignMode('spark', 'standard');
    setSparkMode('standard');
  };

  return (
    <div className="flex flex-col gap-3">
      <label className="text-sm text-ink">{t('settings.spark_default_mode')}</label>
      <SignModeToggle actionType="spark" value={sparkMode} onChange={handleChange} />
      <div>
        <Button variant="secondary" size="sm" onClick={handleReset}>
          {t('settings.reset_to_standard')}
        </Button>
      </div>
    </div>
  );
}
